import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AlertTriangle, HelpCircle, X } from "lucide-react";

const ConfirmContext = createContext(() => Promise.resolve(false));

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback(
    (opts) => new Promise((resolve) => setState({ ...opts, resolve })),
    []
  );

  const close = useCallback((val) => {
    setState((s) => { s && s.resolve(val); return null; });
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  const danger = state?.danger;
  const Icon = danger ? AlertTriangle : HelpCircle;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-dialog">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => close(false)} />
          <div className="relative w-full max-w-md bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl overflow-hidden scale-in">
            <div className="p-5">
              <div className="flex items-start gap-3.5">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${danger ? "bg-red-500/10 border-red-500/30" : "bg-[#c00000]/10 border-[#c00000]/30"}`}>
                  <Icon className={`w-5 h-5 ${danger ? "text-red-600" : "text-[#c00000]"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-base text-gray-900">{state.title}</div>
                  <p className="mt-1 text-sm text-gray-600 leading-relaxed">{state.message}</p>
                </div>
                <button onClick={() => close(false)} className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2 px-5 py-3.5 bg-black/20 border-t border-gray-100">
              <button
                data-testid="confirm-cancel"
                onClick={() => close(false)}
                className="flex-1 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                {state.cancelLabel || "Abbrechen"}
              </button>
              <button
                data-testid="confirm-ok"
                onClick={() => close(true)}
                autoFocus
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                  danger
                    ? "bg-red-500 hover:bg-red-400 text-white"
                    : "bg-[#c00000] hover:bg-[#c00000] text-white"
                }`}
              >
                {state.confirmLabel || "Bestätigen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
